// src/services/suppliersService.ts
import axios from "axios";
import { API_BASE_URL } from "../config";
import { getAuthToken } from "./authService";

export type SupplierListItem = {
  id?: number;
  name?: string;
  email?: string;
  phone?: string;
  national_ID?: string;
  commercialRegistrationNo?: string;
  commercial_Registration_No?: string;
  city_Id?: number;
  state_Id?: number;
};

export type CreateSupplierBody = {
  name: string;
  email?: string;
  phone?: string;
  isSupplier: boolean;
  national_ID?: string;
  commercial_Registration_No?: string;
  city_Id?: number;
  state_Id?: number;
  isMaintainanceContractor: boolean;
  creation_Platform: string;
};

type ListSuppliersArgs = {
  commercialRegistrationNo?: string;
  name?: string;
  pageNo?: number;
  pageSize?: number;
};

function authClient() {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    withCredentials: false,
    validateStatus: (s) => s >= 200 && s < 500,
  });

  const tok = getAuthToken();
  if (tok) client.defaults.headers.common.Authorization = `Bearer ${tok}`;
  return client;
}

function extractOdooMessage(data: any): string | null {
  // Handles: {result:{success:false,message:".."}}
  const msg =
    data?.result?.message ??
    data?.result?.error ??
    data?.message ??
    data?.error ??
    null;

  return typeof msg === "string" && msg.trim() ? msg.trim() : null;
}

export async function listSuppliers(args: ListSuppliersArgs): Promise<SupplierListItem[]> {
  const res = await authClient().get("/api/donations/suppliers/list", {
    params: {
      commercialRegistrationNo: args.commercialRegistrationNo,
      name: args.name,
      pageNo: args.pageNo ?? 1,
      pageSize: args.pageSize ?? 25,
    },
  });

  if (res.status >= 400) {
    const msg = extractOdooMessage(res.data) || `Request failed (${res.status})`;
    throw new Error(msg);
  }

  // Support {data:[...]}, {items:[...]}, direct [...]
  const raw =
    res.data?.data ??
    res.data?.result?.data ??
    res.data?.items ??
    (Array.isArray(res.data) ? res.data : null);
  return Array.isArray(raw) ? raw : [];
}

function extractServerError(data: any, fallback: string): string {
  if (!data || typeof data !== "object") return fallback;
  const msg =
    data.detail ??
    data.message ??
    data.error ??
    data.title ??
    extractOdooMessage(data) ??
    (Array.isArray(data.errors) ? data.errors.join("; ") : null) ??
    (typeof data === "object" && data.errors && typeof data.errors === "object"
      ? JSON.stringify(data.errors)
      : null);
  return typeof msg === "string" && msg.trim() ? msg.trim() : fallback;
}

export async function createSupplier(body: CreateSupplierBody): Promise<any> {
  // بناء payload بصيغ متعددة (camelCase + PascalCase) لضمان توافق الباك إند ASP.NET
  const payload: Record<string, unknown> = {
    name: body.name,
    Name: body.name,
    isSupplier: body.isSupplier,
    IsSupplier: body.isSupplier,
    isMaintainanceContractor: body.isMaintainanceContractor,
    IsMaintainanceContractor: body.isMaintainanceContractor,
    creation_Platform: body.creation_Platform,
    Creation_Platform: body.creation_Platform,
  };
  if (body.email != null && body.email.trim() !== "") {
    payload.email = body.email.trim();
    payload.Email = body.email.trim();
  }
  if (body.phone != null && body.phone.trim() !== "") {
    payload.phone = body.phone.trim();
    payload.Phone = body.phone.trim();
  }
  if (body.national_ID != null && body.national_ID.trim() !== "") {
    payload.national_ID = body.national_ID.trim();
    payload.National_ID = body.national_ID.trim();
  }
  if (body.commercial_Registration_No != null && body.commercial_Registration_No.trim() !== "") {
    payload.commercial_Registration_No = body.commercial_Registration_No.trim();
    payload.Commercial_Registration_No = body.commercial_Registration_No.trim();
  }
  if (body.city_Id != null && body.city_Id > 0) {
    payload.city_Id = body.city_Id;
    payload.City_Id = body.city_Id;
  }
  if (body.state_Id != null && body.state_Id > 0) {
    payload.state_Id = body.state_Id;
    payload.State_Id = body.state_Id;
  }

  const res = await authClient().post("/api/donations/suppliers/create", payload);

  // HTTP error
  if (res.status >= 400) {
    if (process.env.NODE_ENV === "development" && res.status >= 500) {
      console.error("[createSupplier] Server error response:", res.data);
    }
    const msg = extractServerError(res.data, `Request failed with status code ${res.status}`);
    throw new Error(msg);
  }

  // عند HTTP 200 نُرجع الاستجابة كاملة — لا نرمي خطأ لأن الإنشاء قد يكون نجح
  // (بعض الـ APIs تُرجع success:false رغم نجاح الإنشاء أو بصيغة مختلفة)
  return res.data;
}